import React, { useEffect, useRef, useState } from 'react';
import { CafClusterSample, FinalResponseConfirmation, FinalResponsePreview, confirmFinalResponse, getEmailPreview, previewFinalResponse } from '../../api/cafCorpus';
import { Modal } from './Overlay';

const valueLabel = (value: boolean | null) => value === null ? 'Unknown' : value ? 'Yes' : 'No';
const errorText = (error: unknown) => {
  const text = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof text === 'string' ? text : 'Could not load the preview. Try again.';
};
export const FinalResponseModal: React.FC<{ sample: CafClusterSample; onClose: () => void; onSaved: (sample: CafClusterSample) => void }> = ({ sample, onClose, onSaved }) => {
  const [value, setValue] = useState<boolean | null>(sample.final_response_value_known === false ? null : sample.has_final_response);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<FinalResponsePreview | null>(null);
  const [request, setRequest] = useState<FinalResponseConfirmation | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState('');
  const confirmButton = useRef<HTMLButtonElement>(null);
  const choice = useRef<HTMLInputElement>(null);
  useEffect(() => { if (preview) confirmButton.current?.focus(); else choice.current?.focus(); }, [preview]);
  const loadPreview = async () => {
    if (value === null) return;
    setBusy(true); setProblem('');
    try {
      const result = await previewFinalResponse(sample.id, { has_final_response: value, reason: reason.trim() });
      setPreview(result);
      setRequest({ has_final_response: result.after, reason: result.reason,
        expected_storage: result.expected_storage, expected_row_version: result.expected_row_version,
        expected_source_hash: result.expected_source_hash, expected_protected_hash: result.expected_protected_hash,
        idempotency_key: crypto.randomUUID() });
    } catch (error) { setProblem(errorText(error)); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    if (!request) return;
    setBusy(true); setProblem('');
    let acknowledged = false;
    try {
      await confirmFinalResponse(sample.id, request);
      acknowledged = true;
      // The response can be a replay of an older judgment. Render the fresh
      // source read, never a durable optimistic override of its stored value.
      const fresh = await getEmailPreview(sample.id);
      onSaved(fresh); onClose();
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (!pending && !acknowledged && status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        setPreview(null); setRequest(null); setProblem(errorText(error));
      } else {
        setPending(true);
        setProblem('The result is not confirmed yet. Retry to check the same judgment and refresh this email.');
      }
    } finally { setBusy(false); }
  };
  return <Modal title="Review final-response status" onClose={onClose} busy={busy || pending} footer={<>
    {!pending && <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" disabled={busy} onClick={onClose}>Cancel</button>}
    {preview ? <button ref={confirmButton} type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={busy} onClick={() => { void confirm(); }}>{busy ? 'Checking…' : pending ? 'Retry confirmation' : 'Confirm status'}</button>
      : <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={busy || value === null || reason.trim().length < 3} onClick={() => { void loadPreview(); }}>{busy ? 'Loading…' : 'Preview change'}</button>}
  </>}>
    <p>{sample.subject || '(No subject)'}</p>
    <p>This records a status judgment for this email. It does not change the email wording, its group membership or label, or rerun automation.</p>
    {problem && <p role="alert">{problem}</p>}
    {preview ? <>
      <p>Final response: <strong>{valueLabel(preview.before)} → {valueLabel(preview.after)}</strong></p>
      <p>Reason: {preview.reason}</p>
      <p>This records your judgment on this source email. {preview.expected_storage === 'archived' && 'The email stays archived.'}</p>
      {!pending && <button type="button" className="cur-linkish" disabled={busy} onClick={() => { setPreview(null); setRequest(null); }}>Edit choice</button>}
    </> : <>
      <fieldset disabled={busy}><legend>Does this email or any of its attachments contain a final response?</legend>
        <label><input ref={choice} type="radio" name="final-response" checked={value === true} onChange={() => setValue(true)} /> Yes</label>{' '}
        <label><input type="radio" name="final-response" checked={value === false} onChange={() => setValue(false)} /> No</label>
      </fieldset>
      <label>Reason<textarea className="cur-input" value={reason} maxLength={500} disabled={busy} onChange={(event) => setReason(event.target.value)} /></label>
    </>}
  </Modal>;
};
